import React, { useState } from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import {
  Box,
  Button,
  Dialog,
  H1,
  H3,
  HStack,
  ShimmerText,
  Spinner,
  Text,
  VStack,
  VisuallyHidden,
} from '@betterment/samba-react';
import { ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';

import { AccountHighlight } from './AccountHighlight';
import { EditAssumptionsBanner, EditAssumptionsToggleTip } from './EditAssumptionsBanner';
import { GradientBorderBox } from './GradientBorderBox';
import { OpenAccountButton } from './OpenAccountButton';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import AiOrb from '../assets/icon_ai_orb.svg';
import { CreateAccountRecommendationDocument } from '../graphql/mutation.createAccountRecommendation.generated';
import {
  AccountRecommendationOptionDocument,
  AccountRecommendationOptionQuery,
} from '../graphql/query.accountRecommendationOption.generated';

type AccountRecommendationOption = NonNullable<AccountRecommendationOptionQuery['accountRecommendationOption']>;
type ProductOnboardingOption = AccountRecommendationOption['recommendedProductOnboardingOption'];
type RecommendationState = { option: ProductOnboardingOption; isSkipEligible: boolean; reasoning: string | null };

type UseAccountRecommendationProps = { initialRecommendationOption: AccountRecommendationOption };
const useAccountRecommendation = (props: UseAccountRecommendationProps) => {
  const { initialRecommendationOption } = props;
  const [currentRecommendation, setCurrentRecommendation] = useState<RecommendationState>({
    option: initialRecommendationOption.recommendedProductOnboardingOption,
    isSkipEligible: initialRecommendationOption.isRecommendedOptionSkipEligible,
    reasoning: null,
  });
  const [skippedRecommendations, setSkippedRecommendations] = useState<RecommendationState[]>([]);
  const [isCreationLoading, setIsCreationLoading] = useState<boolean>(false);
  const [isSkipLoading, setIsSkipLoading] = useState<boolean>(false);
  const [currentError, setError] = useState<Error | null>(null);
  const [mutate] = useGraphQLMutation(CreateAccountRecommendationDocument);

  if (currentError) {
    throw currentError;
  }

  const createAccountRecommendation = async ({ skipCurrent = false }: { skipCurrent: boolean }) => {
    const setLoading = skipCurrent ? setIsSkipLoading : setIsCreationLoading;
    setLoading(true);
    try {
      const optionsToSkip = skipCurrent ? [currentRecommendation, ...skippedRecommendations] : skippedRecommendations;
      const skippedProductOnboardingOptions = optionsToSkip.map(s => s.option.id);
      const mutationResult = await mutate({ variables: { skippedProductOnboardingOptions } });
      const result = mutationResult.data?.createAccountRecommendation.result;
      if (result) {
        if (skipCurrent) {
          setSkippedRecommendations([...skippedRecommendations, currentRecommendation]);
        }
        setCurrentRecommendation({
          option: result.productOnboardingOption,
          isSkipEligible: result.isOptionSkipEligible,
          reasoning: result.reasoning,
        });
      }
    } catch (error) {
      setError(Error(`Mutation failed: ${error?.toString()}.`));
    } finally {
      setLoading(false);
    }
  };

  return {
    currentRecommendation,
    isCreationLoading,
    isSkipLoading,
    createAccountRecommendation: async () => createAccountRecommendation({ skipCurrent: false }),
    skipAccountRecommendation: async () => createAccountRecommendation({ skipCurrent: true }),
  };
};

const RecommendationContent: React.FC<{ initialRecommendationOption: AccountRecommendationOption }> = ({
  initialRecommendationOption,
}) => {
  const {
    currentRecommendation,
    isCreationLoading,
    isSkipLoading,
    createAccountRecommendation,
    skipAccountRecommendation,
  } = useAccountRecommendation({ initialRecommendationOption });
  const { option } = currentRecommendation;
  const reasonsToBelieve = useFallbackKeys(option.reasonsToBelieve);

  return (
    <VStack css={{ gap: 'space.300' }}>
      <VStack css={{ gap: 'space.100' }}>
        <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
          <Text variant="smallCaps">RECOMMENDATION</Text>
          <EditAssumptionsToggleTip />
        </HStack>
        <H1 variant="brandTitle" color="text">
          {option.name}
        </H1>
        <Text variant="text100" color="text">
          {option.shortDescription}
        </Text>
        {reasonsToBelieve.map(({ item, key }) => (
          <AccountHighlight key={key} text={item} />
        ))}
      </VStack>
      {currentRecommendation.reasoning || isCreationLoading ? (
        <VStack css={{ gap: 'space.200', width: '100%' }}>
          <GradientBorderBox style={{ width: '100%' }}>
            <VStack css={{ padding: 'space.200', gap: 'space.200', width: '100%' }}>
              <HStack css={{ gap: 'space.100', alignItems: 'center' }}>
                <img src={AiOrb} alt="AI Icon" width="20px" height="20px" />
                <H3 variant="title100" color="text">
                  Why this account?
                </H3>
              </HStack>
              {currentRecommendation.reasoning ? (
                <Text
                  variant="text50"
                  color="text"
                  dangerouslySetInnerHTML={{ __html: currentRecommendation.reasoning }}
                />
              ) : (
                <VStack css={{ gap: 'space.100', width: '100%' }}>
                  <ShimmerText textStyle="text50" css={{ width: '100%' }} />
                  <ShimmerText textStyle="text50" css={{ width: '100%' }} />
                  <Box css={{ maxWidth: '300px' }}>
                    <ShimmerText textStyle="text50" />
                  </Box>
                </VStack>
              )}
              <Text variant="text25" color="text.weak">
                AI-summary based on your financial profile.{' '}
                <SambaAutoLink to="/foo" target="_blank">
                  Learn more
                </SambaAutoLink>
              </Text>
            </VStack>
          </GradientBorderBox>
          <EditAssumptionsBanner />
        </VStack>
      ) : (
        <VStack css={{ gap: 'space.200' }}>
          <GradientBorderBox borderRadius={20}>
            <Button
              variant="textSecondary"
              css={{ borderRadius: '20px', textDecoration: 'none', background: 'none', padding: '1px' }}
              onClick={() => createAccountRecommendation()}
            >
              <HStack
                css={{
                  px: 'space.150',
                  py: 'space.100',
                  borderRadius: '20px',
                  background: 'background.surface',
                  gap: 'space.50',
                  alignItems: 'center',
                }}
              >
                <img src={AiOrb} alt="AI Icon" />
                <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
                  Why this account?
                </Text>
              </HStack>
            </Button>
          </GradientBorderBox>
          <Text variant="text25">
            Generates an AI summary based on your financial profile. We never use your data to train our models.{' '}
            <SambaAutoLink to="/foo" target="_blank">
              Learn more
            </SambaAutoLink>
          </Text>
        </VStack>
      )}
      <HStack css={{ paddingTop: 'space.100', gap: 'space.100', alignItems: 'center' }}>
        <OpenAccountButton option={option} isEnabled={!isSkipLoading} />
        {currentRecommendation.isSkipEligible && (
          <Button variant="ghostPrimary" loading={isSkipLoading} onClick={() => skipAccountRecommendation()}>
            Next recommendation
          </Button>
        )}
      </HStack>
    </VStack>
  );
};

const Content: React.FC = () => {
  const { data: optionData } = useGraphQLQuery(AccountRecommendationOptionDocument, { fetchPolicy: 'network-only' });

  const initialRecommendationOption = optionData?.accountRecommendationOption;

  if (initialRecommendationOption)
    return <RecommendationContent initialRecommendationOption={initialRecommendationOption} />;

  return (
    <Box css={{ justifyItems: 'center', alignContent: 'center', padding: 'space.400', minHeight: '400px' }}>
      <Spinner size="lg" />
    </Box>
  );
};

export const AccountRecommendationModal: React.FC = () => {
  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <GradientBorderBox borderRadius={20} role="button" tabIndex={0} style={{ width: '100%' }}>
          <HStack
            css={{
              px: 'space.200',
              py: 'space.100',
              gap: 'space.50',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <img src={AiOrb} alt="AI Icon" />
            <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
              Get a personalized recommendation
            </Text>
          </HStack>
        </GradientBorderBox>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ p: 'space.400', width: '60%', maxWidth: '800px' }}>
        <VisuallyHidden>
          <Dialog.Title>Account recommendation</Dialog.Title>
          <Dialog.Description>Your account recommendation</Dialog.Description>
        </VisuallyHidden>
        <ErrorBoundary fallbackType="partial" feature="account recommendation" owner="agents">
          <IdempotencyProviderWithFeatureRegistry featureName="createAccountRecommendation">
            <Content />
          </IdempotencyProviderWithFeatureRegistry>
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog>
  );
};
